import type { CSSProperties } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Card, PageState } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { useAdminDashboard } from '../../api/admin';

const muted: CSSProperties = { color: 'var(--color-text-muted)' };

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        minWidth: 140,
      }}
    >
      <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>{value}</div>
      <div style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>{label}</div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { isPlatformAdmin } = useAuth();
  const dashboard = useAdminDashboard();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Platform Dashboard</h1>

      {dashboard.isPending ? (
        <PageState kind="loading" message="Loading…" />
      ) : dashboard.isError ? (
        <PageState
          kind="error"
          message={dashboard.error.message}
          onRetry={() => void dashboard.refetch()}
        />
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <StatCard label="Tenants" value={dashboard.data.totals.tenants} />
            <StatCard label="Members" value={dashboard.data.totals.members} />
            <StatCard label="Conversations" value={dashboard.data.totals.conversations} />
            <StatCard label="AI quotations" value={dashboard.data.totals.quotations} />
            <StatCard
              label="AI images processed"
              value={dashboard.data.totals.aiImageSuccess + dashboard.data.totals.aiImageFailed}
            />
            <StatCard label="Appointments" value={dashboard.data.totals.appointments} />
            <StatCard label="Follow-ups" value={dashboard.data.totals.followUps} />
          </div>

          <Card title="By tenant">
            <div style={{ overflowX: 'auto' }}>
              <table className="ne-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Status</th>
                    <th>Members</th>
                    <th>Conversations</th>
                    <th>AI quotations</th>
                    <th>AI images (ok/failed)</th>
                    <th>Appointments</th>
                    <th>Follow-ups</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.data.tenants.map((t) => (
                    <tr key={t.tenantId}>
                      <td>
                        <strong>{t.name}</strong>
                        <div style={{ ...muted, fontSize: 'var(--font-size-sm)' }}>{t.slug}</div>
                      </td>
                      <td>
                        <span className="ne-badge">{t.status}</span>
                      </td>
                      <td>{t.memberCount}</td>
                      <td>{t.conversations}</td>
                      <td>{t.quotations}</td>
                      <td>
                        {t.aiImageSuccess}/{t.aiImageFailed}
                      </td>
                      <td>{t.appointments}</td>
                      <td>{t.followUps}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/tenants">Tenant Management</Link> · <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
